
import React from 'react';
import { Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PricingPage: React.FC = () => {
  const tiers = [
    {
      name: 'Foundation',
      price: 1500,
      description: 'Establish your presence and start receiving qualified leads.',
      features: [
        'Standard Profile',
        'Up to 5 Health Authorities',
        'Up to 5 Therapeutic Areas',
        '5 Qualified RFQ Responses/mo',
        'Quarterly Analytics Report',
        'Verified Badge (Basic)'
      ],
      notIncluded: [
        'Priority Search Placement',
        'Detailed Case Studies',
        'API Access',
        'Video Testimonials'
      ],
      cta: 'Start Free Trial',
      highlighted: false
    },
    {
      name: 'Professional',
      price: 3500,
      description: 'Maximize visibility and showcase deep specialization.',
      features: [
        'Enhanced Profile with Case Studies',
        'Unlimited Health Authorities',
        'Unlimited Therapeutic Areas',
        'Unlimited RFQ Responses',
        'Monthly Market Intelligence',
        'Priority Search Placement',
        'Verified Approval Track Record'
      ],
      notIncluded: [
        'API Access',
        'Dedicated Account Manager'
      ],
      cta: 'Get Started',
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: 7500,
      description: 'For large consultancies requiring integration and dominance.',
      features: [
        'All Professional Features',
        'Top-Tier Search Ranking',
        'Video Testimonials & Rich Media',
        'White-label Capability Deck',
        'API Access for CRM Integration',
        'Dedicated Account Manager',
        'Sponsored Content Slots'
      ],
      notIncluded: [],
      cta: 'Contact Sales',
      highlighted: false
    }
  ];

  return (
    <div className="bg-slate-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-base font-semibold text-blue-600 tracking-wide uppercase">Plans & Pricing</h2>
          <p className="mt-2 text-3xl font-extrabold text-slate-900 sm:text-4xl">
            Grow your regulatory consultancy
          </p>
          <p className="mt-4 text-xl text-slate-500">
            Join 500+ firms using RegulatorySherpas to showcase expertise and close high-value biotech partnerships.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier) => (
            <div key={tier.name} className={`relative bg-white rounded-2xl shadow-xl flex flex-col ${tier.highlighted ? 'ring-2 ring-blue-500 scale-105 z-10' : 'border border-slate-200'}`}>
              {tier.highlighted && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide">
                  Most Popular
                </div>
              )}
              <div className="p-8 flex-grow">
                <h3 className="text-xl font-semibold text-slate-900">{tier.name}</h3>
                <p className="mt-4 flex items-baseline text-slate-900">
                  <span className="text-5xl font-extrabold tracking-tight">${tier.price.toLocaleString()}</span>
                  <span className="ml-1 text-xl font-semibold text-slate-500">/month</span>
                </p>
                <p className="mt-6 text-slate-500">{tier.description}</p>

                <ul className="mt-6 space-y-4">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex">
                      <Check className="flex-shrink-0 w-5 h-5 text-green-500" />
                      <span className="ml-3 text-slate-500">{feature}</span>
                    </li>
                  ))}
                  {tier.notIncluded.map((feature) => (
                    <li key={feature} className="flex">
                      <X className="flex-shrink-0 w-5 h-5 text-slate-300" />
                      <span className="ml-3 text-slate-400 line-through">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-8 bg-slate-50 rounded-b-2xl border-t border-slate-100">
                <Link
                  to={`/signup?role=consultant&plan=${tier.name.toLowerCase()}`}
                  className={`w-full block text-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white transition-colors ${tier.highlighted ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-800 hover:bg-slate-900'}`}
                >
                  {tier.cta}
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
           <p className="text-slate-500">
             Are you an independent consultant or freelancer? <a href="#" className="text-blue-600 font-medium hover:underline">Check out our individual plans.</a>
           </p>
        </div>
      </div>
    </div>
  );
};
